import { useMemo, useState } from "react";
import cn from "classnames";
import { Select } from "components/ui/Select";
import { RateCard } from "./RateCard";
import type { RateMode, RateRecord } from "./rates.model";
import { countByMode, countByStatus, EQUIPMENT_OPTIONS, filterRates } from "./rates.model";

type RatesOverviewProps = {
  rates: RateRecord[];
  onAdd: () => void;
};

export const RatesOverview = ({ rates, onAdd }: RatesOverviewProps) => {
  const counts = countByStatus(rates);
  const modes = countByMode(rates);
  const [mode, setMode] = useState<"all" | RateMode>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [equipment, setEquipment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const shown = useMemo(() => {
    const text = [from, to].filter(Boolean).join(" ");

    return filterRates(
      rates.filter((rate) => rate.status === "ready"),
      { mode, text: submitted ? text : "", equipment: submitted ? equipment : "" }
    );
  }, [rates, mode, from, to, equipment, submitted]);

  return (
    <div className="pc-pane">
      <div className="nw__hd">
        <div>
          <h1 className="nw__h">Logistics Data</h1>
          <p className="nw__t">Manage your freight rates across all transport modes.</p>
        </div>
        <button type="button" className="rd-add" onClick={onAdd}>
          <span>+</span> Add Logistics Data
        </button>
      </div>

      <div className="rd-sum">
        <div className="rd-s">
          <b>{counts.ready}</b>
          <span>Total Active Rates</span>
        </div>
        <div className="rd-s">
          <b>{counts.review}</b>
          <span>Needs Information</span>
        </div>
        <div className="rd-s">
          <b>{rates.length}</b>
          <span>Parsed Records</span>
        </div>
      </div>

      <div className="rd-modes">
        <article className="rd-m">
          <div className="rd-m__t">Sea FCL</div>
          <b>{modes.fcl}</b>
          <span>active rates</span>
        </article>
        <article className="rd-m">
          <div className="rd-m__t">Sea LCL</div>
          <b>{modes.lcl}</b>
          <span>active rates</span>
        </article>
        <article className="rd-m">
          <div className="rd-m__t">Air Rates</div>
          <b>{modes.air}</b>
          <span>active rates</span>
        </article>
        <article className="rd-m">
          <div className="rd-m__t">Land Rates</div>
          <b>{modes.land}</b>
          <span>active rates</span>
        </article>
      </div>

      <div className="nw-list__hd">
        <div className="pcx__h">Active Rates</div>
      </div>

      <form
        className="rdf"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
        }}
      >
        <div className="rdf__modes" role="group" aria-label="Shipping mode">
          {(["all", "ocean", "air"] as const).map((key) => (
            <button
              key={key}
              type="button"
              className={cn("rdf__mode", { "is-on": mode === key })}
              onClick={() => setMode(key)}
            >
              {key === "all" ? "All" : key === "ocean" ? "Sea" : "Air"}
            </button>
          ))}
        </div>
        <div className="rdf__grid">
          <div className="rdf__f">
            <label htmlFor="rdf-from">From</label>
            <input
              id="rdf-from"
              type="text"
              value={from}
              placeholder="Port, airport or city"
              autoComplete="off"
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="rdf__f">
            <label htmlFor="rdf-to">To</label>
            <input
              id="rdf-to"
              type="text"
              value={to}
              placeholder="Port, airport or city"
              autoComplete="off"
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <div className="rdf__f">
            <span className="rdf__lb">Container type</span>
            <Select ariaLabel="Container type" value={equipment} options={EQUIPMENT_OPTIONS} onChange={setEquipment} />
          </div>
          <div className="rdf__f">
            <label htmlFor="rdf-date">Departure date</label>
            <input id="rdf-date" type="date" />
          </div>
          <button type="submit" className="rdf__go">
            Search Rates
          </button>
        </div>
      </form>

      <div className="rt-list rd-cards">
        {shown.map((rate) => (
          <RateCard key={rate.id} rate={rate} variant="overview" />
        ))}
      </div>
      {shown.length === 0 && <p className="rt-empty">No active rates yet — import a tariff to get started.</p>}
    </div>
  );
};
